import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import apiClient from '../../services/api/apiClient';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';

function Step4ThemeFit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editAccountId = searchParams.get('edit');
  const isEditMode = !!editAccountId;

  const [selectedTheme, setSelectedTheme] = useState(null);
  const [customTheme, setCustomTheme] = useState('');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [mode, setMode] = useState(null); // 'analysis' or 'input'
  const [themeProposals, setThemeProposals] = useState([]);
  const [accountInfo, setAccountInfo] = useState(null);

  useEffect(() => {
    if (isEditMode && editAccountId) {
      loadAccountSettings(editAccountId);
    } else {
      checkAccountAndAnalyze();
    }
  }, [isEditMode, editAccountId]);

  const loadAccountSettings = async (accountId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/user/social-accounts/account-settings/id/${accountId}`);
      if (response.data.success && response.data.data.settings) {
        const settings = response.data.data.settings;
        if (settings.excluded_theme) {
          setSelectedTheme(settings.excluded_theme);
          setMode('analysis');
        } else if (settings.target_theme) {
          setCustomTheme(settings.target_theme);
          setMode('input');
        } else {
          setMode('input');
        }
      } else {
        setMode('input');
      }
    } catch (error) {
      console.error('載入設定失敗:', error);
      setMode('input');
    } finally {
      setLoading(false);
    }
  };

  const checkAccountAndAnalyze = async () => {
    try {
      setLoading(true);

      // 0. 檢查是否有快取的分析結果
      const cachedAnalysis = localStorage.getItem('onboarding_theme_analysis');
      if (cachedAnalysis) {
        try {
          const themes = JSON.parse(cachedAnalysis);
          if (themes && themes.length > 0) {
            console.log('✅ 使用快取的主題分析結果');
            setMode('analysis');
            setThemeProposals(themes);

            // 獲取帳號信息用於顯示
            const accountsResponse = await apiClient.get('/user/social-accounts/account-settings');
            const accounts = accountsResponse.data.data || [];
            const activeAccount = accounts.find(acc => acc.status === 'active' && acc.platform === 'threads');
            if (activeAccount) {
              setAccountInfo(activeAccount);
            }

            setLoading(false);
            return;
          }
        } catch (e) {
          console.error('解析快取分析失敗:', e);
        }
      }

      // 1. 檢查是否有連接的帳號
      const accountsResponse = await apiClient.get('/user/social-accounts/account-settings');

      const accounts = accountsResponse.data.data || [];
      const activeAccount = accounts.find(acc => acc.status === 'active' && acc.platform === 'threads');

      if (!activeAccount) {
        // 沒有帳號，使用輸入模式
        setMode('input');
        setLoading(false);
        return;
      }

      setAccountInfo(activeAccount);

      // 2. 獲取帳號的貼文數據
      const postsResponse = await apiClient.get(`/content/posts?accountId=${activeAccount.id}&limit=50&status=published`);

      const posts = postsResponse.data.posts || [];

      // 過濾出有文字內容的帖子
      const postsWithContent = posts.filter(post =>
        post.content_text && post.content_text.trim() &&
        post.platform === 'threads'
      );

      if (postsWithContent.length < 10) {
        // 貼文數 < 10，使用輸入模式
        setMode('input');
        setLoading(false);
        return;
      }

      // 3. 貼文數 >= 10，分析並生成主題提案
      setMode('analysis');
      setAnalyzing(true);

      const analysisResponse = await apiClient.post('/ai/analyze-account-themes', {
        accountId: activeAccount.id,
        posts: postsWithContent.slice(0, 50) // 最多分析 50 篇貼文
      });

      if (analysisResponse.data.success) {
        const themes = analysisResponse.data.themes || [];
        setThemeProposals(themes);
        // 儲存到 localStorage 以供下次使用
        localStorage.setItem('onboarding_theme_analysis', JSON.stringify(themes));
      } else {
        // 分析失敗，fallback 到輸入模式
        setMode('input');
      }

    } catch (error) {
      console.error('檢查帳號或分析失敗:', error);
      // 發生錯誤，使用輸入模式
      setMode('input');
    } finally {
      setLoading(false);
      setAnalyzing(false);
    }
  };

  const handleSelect = (themeId) => {
    setSelectedTheme(themeId);
  };

  const handleNext = () => {
    if (mode === 'analysis' && selectedTheme) {
      localStorage.setItem('onboarding_excluded_theme', selectedTheme);
      if (isEditMode && editAccountId) {
        navigate(`/onboarding/step5?edit=${editAccountId}`);
      } else {
        navigate('/onboarding/step5');
      }
    } else if (mode === 'input' && customTheme.trim()) {
      localStorage.setItem('onboarding_target_theme', customTheme.trim());
      if (isEditMode && editAccountId) {
        navigate(`/onboarding/step5?edit=${editAccountId}`);
      } else {
        navigate('/onboarding/step5');
      }
    }
  };

  const handleBack = () => {
    if (isEditMode && editAccountId) {
      navigate(`/onboarding/step3?edit=${editAccountId}`);
    } else {
      navigate('/onboarding/step3');
    }
  };

  const illustration = (
    <div className="w-full max-w-md mx-auto">
      <div className="relative flex items-center justify-center">
        {/* Color palette circles */}
        <div className="relative">
          {/* Main circle container */}
          <div className="relative w-64 h-64 rounded-full bg-white/60 backdrop-blur-sm shadow-lg flex items-center justify-center">
            {/* Color dots arranged in circle */}
            <div className="relative w-48 h-48">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-gradient-to-br from-red-400 to-red-500 shadow-md"></div>
              <div className="absolute top-1/4 right-0 w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 shadow-md"></div>
              <div className="absolute bottom-1/4 right-0 w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-400 shadow-md"></div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 shadow-md"></div>
              <div className="absolute bottom-1/4 left-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-violet-400 shadow-md"></div>
              <div className="absolute top-1/4 left-0 w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-rose-400 shadow-md"></div>

              {/* Center icon */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-blue-500 shadow-lg flex items-center justify-center">
                <Icon icon="mdi:palette" className="text-2xl text-white" />
              </div>
            </div>
          </div>

          {/* X mark on one color */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center">
            <Icon icon="mdi:close-thick" className="text-xl text-red-500" />
          </div>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <OnboardingLayout
        currentStep={4}
        totalSteps={6}
        title={analyzing ? "正在分析你的帳號..." : "載入中..."}
        illustration={illustration}
        onBack={handleBack}
        progressColor="#60A5FA"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <Icon icon="line-md:loading-loop" className="text-6xl text-blue-500 mb-4" />
          <p className="text-gray-600">
            {analyzing ? '正在使用 AI 分析您的 Threads 貼文內容...' : '正在載入...'}
          </p>
        </div>
      </OnboardingLayout>
    );
  }

  // Input mode: < 10 posts or no account
  if (mode === 'input') {
    return (
      <OnboardingLayout
        currentStep={4}
        totalSteps={6}
        title="你想經營什麼主題？"
        illustration={illustration}
        onBack={handleBack}
        progressColor="#60A5FA"
      >
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Icon icon="mdi:information-outline" className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-medium mb-1">描述你的內容主題</p>
              <p className="text-gray-600">
                {accountInfo && accountInfo.posts_count < 10
                  ? '您的帳號貼文數量較少，請直接告訴我們您想經營的內容主題。'
                  : '請告訴我們您想經營的內容主題方向。'}
              </p>
            </div>
          </div>
        </div>

        {/* Theme Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            內容主題 *
          </label>
          <textarea
            value={customTheme}
            onChange={(e) => setCustomTheme(e.target.value)}
            placeholder="例如：科技創新與 AI 應用、個人成長與生產力提升、創業心得與商業策略..."
            rows={4}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors resize-none"
          />
          <p className="text-xs text-gray-500 mt-2">
            盡量具體描述，這將幫助 AI 為您生成更精準的內容
          </p>
        </div>

        {/* Example Themes */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <p className="text-xs font-medium text-gray-500 mb-3">範例主題：</p>
          <div className="flex flex-wrap gap-2">
            {['科技創新與 AI', '個人成長', '創業與商業', '生活品味', '健康養生'].map((example, index) => (
              <button
                key={index}
                onClick={() => setCustomTheme(example)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                {example}
              </button>
            ))}
          </div>
        </div>

        {/* Next Button */}
        <div className="flex justify-end mt-8">
          <button
            onClick={handleNext}
            disabled={!customTheme.trim()}
            className={`
              px-8 py-3 font-medium rounded-xl transition-colors flex items-center gap-2
              ${customTheme.trim()
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            Next
            <Icon icon="mdi:arrow-right" className="text-xl" />
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  // Analysis mode: >= 10 posts
  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={6}
      title="哪個主題最不像你？"
      illustration={illustration}
      onBack={handleBack}
      progressColor="#60A5FA"
    >
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon icon="mdi:information-outline" className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-1">AI 分析結果</p>
            <p className="text-gray-600">
              根據您 <strong>@{accountInfo?.account_name}</strong> 的貼文分析，我們識別出以下 3 個主題方向。請選擇<strong>最不符合</strong>您想要的風格，幫助我們更精準定位內容方向。
            </p>
          </div>
        </div>
      </div>

      {/* Theme Proposals */}
      <div className="space-y-4 mb-6">
        {themeProposals.map((theme) => (
          <button
            key={theme.id}
            onClick={() => handleSelect(theme.id)}
            className={`
              w-full p-6 rounded-xl border-2 transition-all text-left
              ${selectedTheme === theme.id
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }
            `}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                ${selectedTheme === theme.id ? 'bg-blue-500' : 'bg-gray-100'}
              `}>
                <Icon
                  icon="mdi:close-circle-outline"
                  className={`text-2xl ${selectedTheme === theme.id ? 'text-white' : 'text-gray-600'}`}
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {theme.title}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {theme.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {theme.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-600"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
              {selectedTheme === theme.id && (
                <Icon icon="mdi:check-circle" className="text-2xl text-blue-500 flex-shrink-0" />
              )}
            </div>

            {/* Sample Posts */}
            <div className="pl-16">
              <p className="text-xs font-medium text-gray-500 mb-2">典型內容範例：</p>
              <ul className="space-y-1">
                {theme.samplePosts.map((post, index) => (
                  <li key={index} className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    <span>{post}</span>
                  </li>
                ))}
              </ul>
            </div>
          </button>
        ))}
      </div>

      {/* Next Button */}
      <div className="flex justify-end mt-8">
        <button
          onClick={handleNext}
          disabled={!selectedTheme}
          className={`
            px-8 py-3 font-medium rounded-xl transition-colors flex items-center gap-2
            ${selectedTheme
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Next
          <Icon icon="mdi:arrow-right" className="text-xl" />
        </button>
      </div>
    </OnboardingLayout>
  );
}

export default Step4ThemeFit;
